import {
  AccessCredentials,
  GenericCredentials,
  AuthProvider,
  AuthProviderForm as AuthProviderFields,
  Session,
  SessionClaims,
} from '../sessions.service';

/**
 * Custom authentication provider implementation.
 *
 * This class provides a custom authentication provider that can be used to authenticate users.
 * It takes in a configuration object that defines the authentication fields, authentication function,
 * session identifier function, and optional refresh function.
 */
export class CustomAuthProvider implements AuthProvider {
  /**
   * The label of the authentication provider.
   */
  label: string;

  /**
   * The icon of the authentication provider.
   */
  icon: string;

  /**
   * Constructor for the CustomAuthProvider class.
   *
   * @param name The name of the authentication provider.
   * @param config The configuration object for the authentication provider.
   */
  constructor(
    public name: string,
    private config: {
      /**
       * The label of the authentication provider (optional).
       */
      label?: string;

      /**
       * The icon of the authentication provider (optional).
       */
      icon?: string;

      /**
       * The authentication fields for the provider.
       */
      fields: AuthProviderFields;

      /**
       * The authentication function that takes in generic credentials and returns a promise with session claims, access token, refresh token, and expiration time.
       *
       * @param fields The generic credentials to authenticate.
       * @returns A promise with the session claims, access token, refresh token, and expiration time.
       */
      authenticate: (
        fields: GenericCredentials
      ) => Promise<{ claims: SessionClaims; accessToken: string; refreshToken?: string; expiresAt: number }>;

      /**
       * The function that generates a session identifier based on the access token and session claims.
       *
       * @param accessToken The access token.
       * @param claims The session claims.
       * @returns The session identifier.
       */
      getSessionIdentifier: (accessToken: string, claims: SessionClaims) => string;

      /**
       * The optional refresh function that takes in a refresh token and returns a promise with session claims, access token, refresh token, and expiration time.
       */
      refresh?: (
        refreshToken: string
      ) => Promise<{ claims: SessionClaims; accessToken: string; refreshToken?: string; expiresAt: number }>;
    }
  ) {
    this.label = config.label ?? name;
    this.icon = config.icon ?? '';
  }

  /**
   * Gets the sign-in fields for the authentication provider.
   *
   * @returns The sign-in fields.
   */
  getSignInFields(): AuthProviderFields {
    return this.config.fields;
  }

  /**
   * Starts a new session with the provided credentials.
   *
   * @param credentials The generic credentials to authenticate.
   * @returns A promise with the new session.
   */
  async startSession(credentials: GenericCredentials): Promise<Session> {
    const { claims, accessToken, refreshToken, expiresAt } = await this.config.authenticate(credentials);

    const id = this.config.getSessionIdentifier(accessToken, claims);

    const authCredentials: GenericCredentials = {
      ...credentials,
    };

    const refreshCredentials: GenericCredentials = refreshToken ? { refreshToken: refreshToken } : {};
    const accessCredentials: AccessCredentials = {
      httpHeaders: {
        Authorization: `Basic ${accessToken}`,
      },
    };

    const prefillInfo = Object.fromEntries(
      this.getSignInFields()
        .filter((field) => field.key in credentials && field.saveForPrefill)
        .map((field) => [field.key, credentials[field.key]])
    );

    return new Session(
      id,
      this.name,
      authCredentials,
      refreshCredentials,
      prefillInfo,
      accessCredentials,
      new Date().getTime(),
      claims,
      expiresAt,
      null,
      null
    );
  }

  /**
   * Refreshes an existing session with a refresh token.
   *
   * @param session The session to refresh.
   * @returns A promise with the refreshed session.
   */
  async refreshSession(session: Session): Promise<Session> {
    if (!this.config.refresh) {
      throw new Error('Refresh callback not defined');
    }

    const refreshToken = (session.refreshCredentials ?? {})['refreshToken'];

    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    const refreshedData = await this.config.refresh(refreshToken);

    const newAuthCredentials = {
      ...(session.authCredentials ?? {}),
      accessToken: refreshedData.accessToken,
    } as GenericCredentials;

    const newRefreshCredentials = refreshedData.refreshToken
      ? {
          refreshToken: refreshedData.refreshToken,
        }
      : null;

    return new Session(
      session.id,
      session.authProvider,
      newAuthCredentials,
      newRefreshCredentials,
      session.prefillInfo,
      {
        httpHeaders: {
          Authorization: `Bearer ${refreshedData.accessToken}`,
        },
      },
      session.startedAt,
      refreshedData.claims,
      refreshedData.expiresAt,
      new Date().getTime(),
      null
    );
  }
}
